'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { Search, QrCode, UserCheck, BarChart2, FileText, Bell, CreditCard, CheckSquare, Gavel, ExternalLink, ChevronRight, X } from 'lucide-react';

interface Stage {
  id: number;
  title: string;
  shortTitle: string;
  description: string;
  icon: React.ElementType;
  color: string;
  moduleLink: string;
  moduleName: string;
  kpis: { label: string; value: string; trend?: string; link?: string; group?: string }[];
}

const stages: Stage[] = [
  {
    id: 1,
    title: 'Discovery (Identification, Mapping and Registration)',
    shortTitle: 'Discovery',
    description: 'Systematically identify, geo-locate, and register all taxable properties across Liberia — including residential, commercial, industrial, vacant land, farmland, and public-land improvements — using field surveys, drone imagery, and AI-assisted detection to build a complete and authoritative property register.',
    icon: Search,
    color: '#f59e0b',
    moduleLink: '/gis-property-hub',
    moduleName: 'GIS Property Hub',
    kpis: [
      { label: 'Property Captured', value: '4,821', trend: '+312', link: '/gis-property-hub' },
      { label: 'Property Registered & Geo-Tagged', value: '4,818', trend: 'AI', link: '/taxpayer-registry' },
      { label: 'Unregistered & Pending Geo-Tagged', value: '3', link: '/gis-property-hub' },
      { label: 'Counties Coverage', value: '15/15', link: '/county-offices' },
    ],
  },
  {
    id: 2,
    title: 'Assessment (Valuation)',
    shortTitle: 'Assessment',
    description: 'Determine the taxable value of each registered property by applying approved valuation methodologies — factoring in property category, location zone, size, use type, and current market conditions — to produce consistent, defensible, and legally compliant assessed values.',
    icon: QrCode,
    color: '#10b981',
    moduleLink: '/gis-property-hub',
    moduleName: 'GIS Property Hub → Valuation',
    kpis: [
      { label: 'Property Valued', value: '$100,000', link: '/gis-property-hub' },
      { label: 'No. of Property Accessed', value: '10', link: '/gis-property-hub' },
      { label: 'Cum. Accessed Value', value: '$1,000,000', link: '/tax-assessment-admin' },
    ],
  },
  {
    id: 3,
    title: 'Rates & Exemptions',
    shortTitle: 'Rates',
    description: 'Apply the legally prescribed tax rates against property current market value. Ensuring every liability calculation is fair, consistent, and fully compliant with the LRA Revenue Code and applicable regulations. Account for exempt properties based on the Liberia revenue code.',
    icon: UserCheck,
    color: '#3b82f6',
    moduleLink: '/taxpayer-registry',
    moduleName: 'Taxpayer Registry',
    kpis: [
      { label: 'Commercial', value: '$1,000,000', link: '/taxpayer-registry', group: 'tax' },
      { label: 'Tax Rate', value: '1.5%', link: '/taxpayer-registry', group: 'tax' },
      { label: 'Tax Amount', value: '$15,000', link: '/revenue-analytics', group: 'tax' },
      { label: 'Properties Exempted', value: '412', link: '/taxpayer-registry', group: 'exemption' },
      { label: 'Potential Cumulative Value', value: '$6,180,000', link: '/revenue-analytics', group: 'exemption' },
      { label: 'Potential Tax Rev. Exempted', value: '$92,700', link: '/revenue-analytics', group: 'exemption' },
    ],
  },
  {
    id: 4,
    title: 'Billing',
    shortTitle: 'Billing',
    description: 'Automatically compute the property tax liability for each assessed parcel based on use classification and tax rate. Generate official demand notices, invoices, or assessment certificates — ensuring every billable property receives a timely, accurate, and legally valid tax bill.',
    icon: BarChart2,
    color: '#8b5cf6',
    moduleLink: '/gis-property-hub',
    moduleName: 'GIS Hub → Tax Bills',
    kpis: [
      { label: 'Billable Properties', value: '4,818', link: '/gis-property-hub' },
      { label: 'Demand Notices Issued', value: '4,818', link: '/gis-property-hub' },
      { label: 'Total Billing Value', value: '$481,800,000', link: '/tax-assessment-admin' },
      { label: 'Projected Tax Revenue', value: '$7,227,000', link: '/revenue-analytics' },
    ],
  },
  {
    id: 5,
    title: 'Taxpayer Education & Engagement',
    shortTitle: 'Education',
    description: 'Proactively inform and engage property owners about their tax obligations, rights, payment options, and deadlines — through multi-channel outreach including SMS, email, WhatsApp, printed notices, community sessions, and the self-service taxpayer portal.',
    icon: FileText,
    color: '#ec4899',
    moduleLink: '/taxpayer-self-service-portal',
    moduleName: 'Taxpayer Portal',
    kpis: [
      { label: 'Bills Generated', value: '4,818', link: '/gis-property-hub' },
      { label: 'Avg. Bill', value: '$100,000', link: '/revenue-analytics' },
      { label: 'Total Assessed', value: '$481.8M', link: '/revenue-analytics' },
    ],
  },
  {
    id: 6,
    title: 'Appeals',
    shortTitle: 'Appeals',
    description: 'Administer a structured, transparent, and time-bound objection and appeals process — enabling property owners to formally dispute assessments or valuations, submit supporting evidence, and receive documented decisions in accordance with LRA procedures.',
    icon: Bell,
    color: '#f97316',
    moduleLink: '/taxpayer-self-service-portal',
    moduleName: 'Taxpayer Portal → Appeals',
    kpis: [
      { label: 'Notices Sent', value: '4,818', link: '/taxpayer-self-service-portal' },
      { label: 'SMS Delivered', value: '3,941', trend: '81.8%', link: '/taxpayer-self-service-portal' },
      { label: 'Portal Views', value: '1,847', link: '/taxpayer-self-service-portal' },
    ],
  },
  {
    id: 7,
    title: 'Payments',
    shortTitle: 'Payments',
    description: 'Facilitate secure, multi-channel property tax payments — including approved commercial banks, mobile money platforms (Orange Money, MTN), online portals, and LRA cashier points — with instant digital receipt issuance and real-time reconciliation against outstanding bills.',
    icon: CreditCard,
    color: '#14b8a6',
    moduleLink: '/collections',
    moduleName: 'Collections',
    kpis: [
      { label: 'Payments Received', value: '$3,208,788', link: '/collections' },
      { label: 'Collection Rate', value: '44.4%', link: '/collections' },
      { label: 'Digital Receipts', value: '2,139', link: '/property-payments-matching' },
    ],
  },
  {
    id: 8,
    title: 'Voluntary Compliance',
    shortTitle: 'Compliance',
    description: 'Monitor and promote self-initiated tax compliance across the property portfolio — tracking paid, unpaid, underpaid, and overdue accounts by area and property type, and deploying targeted incentives, reminders, and AI-driven alerts to maximise voluntary payment rates.',
    icon: CheckSquare,
    color: '#06b6d4',
    moduleLink: '/compliance-engine',
    moduleName: 'Compliance Engine',
    kpis: [
      { label: 'Compliance Rate', value: '44.4%', link: '/compliance-engine' },
      { label: 'Overdue Accounts', value: '2,679', link: '/compliance-engine' },
      { label: 'AI Alerts Active', value: '12', link: '/compliance-engine' },
    ],
  },
  {
    id: 9,
    title: 'Enforcement',
    shortTitle: 'Enforcement',
    description: 'Execute statutory enforcement actions against non-compliant property owners — including penalty assessments, lien placements, field inspection visits, and legal referrals — while updating property records and feeding enforcement outcomes back into the next tax cycle.',
    icon: Gavel,
    color: '#ef4444',
    moduleLink: '/gis-property-hub',
    moduleName: 'GIS Hub → Enforcement',
    kpis: [
      { label: 'Overdue Accounts', value: '2,679', link: '/compliance-engine' },
      { label: 'Outstanding Revenue', value: '$4,018,212', link: '/revenue-analytics' },
      { label: 'Penalties Applied', value: '~$120,546', link: '/revenue-analytics' },
      { label: 'Enforcement Actions', value: '2,679', link: '/gis-property-hub' },
    ],
  },
];

interface PropertyTaxCycleDiagramProps {
  compact?: boolean;
}

export default function PropertyTaxCycleDiagram({ compact = false }: PropertyTaxCycleDiagramProps) {
  const [activeStage, setActiveStage] = useState<Stage | null>(null);
  const [animatedStages, setAnimatedStages] = useState<number[]>([]);
  const [rotating, setRotating] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    stages.forEach((_, i) => {
      timers.push(setTimeout(() => {
        setAnimatedStages(prev => [...prev, i + 1]);
      }, i * 120));
    });
    return () => timers.forEach(clearTimeout);
  }, []);

  const radius = compact ? 130 : 168;
  const cx = compact ? 180 : 230;
  const cy = compact ? 180 : 230;
  const svgSize = compact ? 360 : 460;

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}
      ref={containerRef}
    >
      {/* Header */}
      <div className="px-6 pt-5 pb-4 flex items-center justify-between" style={{ borderBottom: '1px solid var(--dark-border)' }}>
        <div>
          <h2 className="text-lg font-bold" style={{ color: 'var(--dark-text)' }}>
            LRA Property Tax Cycle
          </h2>
          <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>
            9-Stage Digital Intelligence System · Click any stage to explore
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setRotating(r => !r)}
            className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
            style={{
              backgroundColor: rotating ? 'rgba(212,160,23,0.15)' : 'var(--dark-surface)',
              color: rotating ? 'var(--accent)' : 'var(--dark-muted)',
              border: '1px solid var(--dark-border)',
            }}
          >
            {rotating ? '⏸ Pause' : '▶ Animate'}
          </button>
          {!compact && (
            <Link
              href="/property-tax-cycle"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:opacity-85"
              style={{ background: 'linear-gradient(135deg, #b8860b 0%, #d4a017 100%)', color: '#070e1f' }}
            >
              <ExternalLink size={12} />
              Full View
            </Link>
          )}
        </div>
      </div>

      <div className={`flex ${compact ? 'flex-col' : 'flex-col xl:flex-row'} gap-0`}>
        {/* SVG Circular Diagram */}
        <div className="flex items-center justify-center p-6 flex-shrink-0">
          <svg
            width={svgSize}
            height={svgSize}
            viewBox={`0 0 ${svgSize} ${svgSize}`}
            className="overflow-visible"
          >
            {/* Outer ring track */}
            <circle
              cx={cx} cy={cy} r={radius + 24}
              fill="none"
              stroke="rgba(255,255,255,0.04)"
              strokeWidth="48"
            />
            {/* Inner glow ring */}
            <circle
              cx={cx} cy={cy} r={radius}
              fill="none"
              stroke="rgba(212,160,23,0.08)"
              strokeWidth="2"
              strokeDasharray="6 4"
            />

            {/* Animated rotation group */}
            <g style={{
              transformOrigin: `${cx}px ${cy}px`,
              animation: rotating ? 'spin-slow 60s linear infinite' : 'none',
            }}>
              {stages.map((stage, i) => {
                const angle = (i / 9) * 2 * Math.PI - Math.PI / 2;
                const x = cx + radius * Math.cos(angle);
                const y = cy + radius * Math.sin(angle);
                const isActive = activeStage?.id === stage.id;
                const isAnimated = animatedStages.includes(stage.id);
                const IconComp = stage.icon;

                return (
                  <g key={stage.id}>
                    {/* Connector line */}
                    <line
                      x1={cx} y1={cy}
                      x2={x} y2={y}
                      stroke={isActive ? stage.color : 'rgba(255,255,255,0.05)'}
                      strokeWidth={isActive ? 1.5 : 0.5}
                      strokeDasharray={isActive ? 'none' : '3 4'}
                      style={{ transition: 'all 0.3s ease' }}
                    />
                    {/* Stage node outer glow */}
                    {isActive && (
                      <circle
                        cx={x} cy={y} r={28}
                        fill={stage.color}
                        opacity={0.15}
                      />
                    )}
                    {/* Stage node */}
                    <circle
                      cx={x} cy={y} r={isActive ? 22 : 18}
                      fill={isActive ? stage.color : 'var(--dark-elevated, #1a2540)'}
                      stroke={isAnimated ? stage.color : 'rgba(255,255,255,0.1)'}
                      strokeWidth={isActive ? 2.5 : 1.5}
                      style={{
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
                        filter: isActive ? `drop-shadow(0 0 8px ${stage.color}80)` : 'none',
                      }}
                      onClick={() => setActiveStage(isActive ? null : stage)}
                    />
                    {/* Stage number */}
                    <text
                      x={x} y={y - 5}
                      textAnchor="middle"
                      fontSize="9"
                      fontWeight="700"
                      fill={isActive ? '#070e1f' : stage.color}
                      style={{ pointerEvents: 'none', fontFamily: 'monospace' }}
                    >
                      {stage.id}
                    </text>
                    {/* Short label */}
                    <text
                      x={x} y={y + 5}
                      textAnchor="middle"
                      fontSize="6.5"
                      fontWeight="600"
                      fill={isActive ? '#070e1f' : 'rgba(255,255,255,0.7)'}
                      style={{ pointerEvents: 'none' }}
                    >
                      {stage.shortTitle.length > 8 ? stage.shortTitle.slice(0, 8) : stage.shortTitle}
                    </text>
                  </g>
                );
              })}
            </g>

            {/* Center circle */}
            <circle
              cx={cx} cy={cy} r={compact ? 52 : 68}
              fill="var(--dark-surface, #0d1526)"
              stroke="rgba(212,160,23,0.3)"
              strokeWidth="1.5"
            />
            <circle
              cx={cx} cy={cy} r={compact ? 48 : 62}
              fill="none"
              stroke="rgba(212,160,23,0.1)"
              strokeWidth="1"
              strokeDasharray="4 3"
            />
            <text
              x={cx} y={cy - (compact ? 14 : 18)}
              textAnchor="middle"
              fontSize={compact ? '7' : '8'}
              fontWeight="700"
              fill="var(--accent, #d4a017)"
              letterSpacing="0.5"
            >
              LRA PROPERTY
            </text>
            <text
              x={cx} y={cy - (compact ? 4 : 5)}
              textAnchor="middle"
              fontSize={compact ? '7' : '8'}
              fontWeight="700"
              fill="var(--accent, #d4a017)"
              letterSpacing="0.5"
            >
              TAX DIGITAL
            </text>
            <text
              x={cx} y={cy + (compact ? 6 : 8)}
              textAnchor="middle"
              fontSize={compact ? '7' : '8'}
              fontWeight="700"
              fill="var(--accent, #d4a017)"
              letterSpacing="0.5"
            >
              INTELLIGENCE
            </text>
            <text
              x={cx} y={cy + (compact ? 16 : 20)}
              textAnchor="middle"
              fontSize={compact ? '7' : '8'}
              fontWeight="700"
              fill="var(--accent, #d4a017)"
              letterSpacing="0.5"
            >
              SYSTEM
            </text>
          </svg>

          <style>{`
            @keyframes spin-slow {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>

        {/* Stage Detail Panel */}
        <div className="flex-1 min-w-0 p-5 border-t xl:border-t-0 xl:border-l" style={{ borderColor: 'var(--dark-border)' }}>
          {activeStage ? (
            <div className="h-full">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${activeStage.color}20`, border: `1.5px solid ${activeStage.color}50` }}
                  >
                    <activeStage.icon size={18} style={{ color: activeStage.color }} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span
                        className="text-xs font-bold px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: `${activeStage.color}20`, color: activeStage.color }}
                      >
                        Stage {activeStage.id}
                      </span>
                    </div>
                    <h3 className="text-sm font-bold mt-0.5" style={{ color: 'var(--dark-text)' }}>
                      {activeStage.title}
                    </h3>
                  </div>
                </div>
                <button onClick={() => setActiveStage(null)}>
                  <X size={16} style={{ color: 'var(--dark-muted)' }} />
                </button>
              </div>

              <p className="text-xs leading-relaxed mb-4" style={{ color: 'var(--dark-muted)' }}>
                {activeStage.description}
              </p>

              {/* KPIs */}
              {activeStage.kpis.some((kpi) => (kpi as { label: string; value: string; link?: string; trend?: string; group?: string }).group) ? (
                <div className="flex flex-col gap-2 mb-4 min-w-0 w-full overflow-hidden">
                  {/* Row 1: Commercial, Tax Rate, Tax Amount */}
                  <p className="text-xs font-semibold uppercase tracking-wide mb-0.5" style={{ color: 'var(--dark-muted)' }}>Tax Calculation</p>
                  <div className="grid grid-cols-3 gap-2 min-w-0 w-full">
                    {activeStage.kpis
                      .filter((kpi) => (kpi as { label: string; value: string; link?: string; trend?: string; group?: string }).group === 'tax')
                      .map((kpi) => (
                        <div
                          key={kpi.label}
                          className="rounded-lg p-2.5 min-w-0 overflow-hidden"
                          style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}
                        >
                          <p className="text-xs mb-0.5 break-words leading-tight" style={{ color: 'var(--dark-muted)' }}>{kpi.label}</p>
                          <p className="text-sm font-bold font-mono break-all" style={{ color: activeStage.color }}>{kpi.value}</p>
                          {kpi.trend && (
                            <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>{kpi.trend}</p>
                          )}
                        </div>
                      ))}
                  </div>
                  {/* Divider between Row 1 and Row 2 */}
                  <div className="flex items-center gap-2 my-1">
                    <div className="flex-1 h-px" style={{ backgroundColor: 'var(--dark-border)' }} />
                  </div>
                  {/* Row 2: Properties Exempted, Potential Tax Rev. Exempted, Potential Cumulative Value */}
                  <p className="text-xs font-semibold uppercase tracking-wide mb-0.5" style={{ color: 'var(--dark-muted)' }}>Exemption Summary</p>
                  <div className="grid grid-cols-3 gap-2 min-w-0 w-full">
                    {activeStage.kpis
                      .filter((kpi) => (kpi as { label: string; value: string; link?: string; trend?: string; group?: string }).group === 'exemption')
                      .map((kpi) => (
                        <div
                          key={kpi.label}
                          className="rounded-lg p-2.5 min-w-0 overflow-hidden"
                          style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}
                        >
                          <p className="text-xs mb-0.5 break-words leading-tight" style={{ color: 'var(--dark-muted)' }}>{kpi.label}</p>
                          <p className="text-sm font-bold font-mono break-all" style={{ color: activeStage.color }}>{kpi.value}</p>
                          {kpi.trend && (
                            <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>{kpi.trend}</p>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              ) : (
              <div className={`grid gap-2 mb-4 ${activeStage.kpis.length >= 4 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                {activeStage.kpis.map((kpi) => (
                  <div
                    key={kpi.label}
                    className="rounded-lg p-2.5"
                    style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}
                  >
                    <p className="text-xs mb-0.5" style={{ color: 'var(--dark-muted)' }}>{kpi.label}</p>
                    <p className="text-sm font-bold font-mono" style={{ color: activeStage.color }}>{kpi.value}</p>
                    {kpi.trend && (
                      <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>{kpi.trend}</p>
                    )}
                  </div>
                ))}
              </div>
              )}

              {/* Module link */}
              <Link
                href={activeStage.moduleLink}
                className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-xs font-semibold transition-all hover:opacity-85 active:scale-95"
                style={{ background: `linear-gradient(135deg, ${activeStage.color}20, ${activeStage.color}10)`, border: `1px solid ${activeStage.color}30`, color: activeStage.color }}
              >
                <span>Open: {activeStage.moduleName}</span>
                <ChevronRight size={14} />
              </Link>
            </div>
          ) : (
            <div className="h-full flex flex-col justify-between">
              <div>
                <h3 className="text-sm font-bold mb-1" style={{ color: 'var(--dark-text)' }}>
                  Select a Stage
                </h3>
                <p className="text-xs mb-4" style={{ color: 'var(--dark-muted)' }}>
                  Click any node in the cycle to view stage details, live KPIs, and navigate to the relevant module.
                </p>
                <div className="space-y-1.5">
                  {stages.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setActiveStage(s)}
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-all hover:bg-white/5"
                      style={{ border: '1px solid var(--dark-border)' }}
                    >
                      <span
                        className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                        style={{ backgroundColor: `${s.color}20`, color: s.color }}
                      >
                        {s.id}
                      </span>
                      <span className="text-xs font-medium flex-1 truncate" style={{ color: 'var(--dark-text)' }}>
                        {s.title}
                      </span>
                      <ChevronRight size={12} style={{ color: 'var(--dark-muted)' }} />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export { stages };
